import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { AudioTrackTable, AudioTrackTableMessage } from '../../common/AudioTrackTable';
import { useAudioCatalog } from '../../../features/audioCatalog/hooks';
import { useGenerationAssets } from '../state/assetsContext';
import { AUDIO_USAGE_LABELS } from '../state/assetMediaUtils';
import type { GenerationAsset } from '../state/types';

type Props = {
  isOpen: boolean;
  onClose: () => void;
};

/**
 * 運営提供SEカタログ (audio_catalog_items) から参照音声を選ぶピッカー
 * (docs/features/reference-audio-roles-plan.md フェーズ5b・§2.7)。
 *
 * SelectedAssetsPanel から開く。選択した項目は通常の音声素材チップと同じ扱いで
 * selectedAssets へ追加する (addAsset が形式/上限/モード適合をまとめて検証するため、
 * ここでは GenerationAsset を組み立てて渡すだけでよい)。カタログ由来と判別できるよう
 * `sourceKind: 'audio_catalog'` を必ず付与する (buildVideoPayload が
 * `source.kind: 'audio_catalog'` を送るために参照する)。
 */
export function AudioCatalogPickerModal({ isOpen, onClose }: Props) {
  const { data: items = [], isLoading, isError } = useAudioCatalog(isOpen);
  const { selectedAssets, addAsset } = useGenerationAssets();

  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = prevOverflow;
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const selectedCatalogIds = new Set(
    selectedAssets
      .filter((a) => a.usage === 'reference_audio' && a.sourceKind === 'audio_catalog')
      .map((a) => a.id),
  );

  const handleAdd = (item: (typeof items)[number]) => {
    if (!item.preview_url) return;
    const asset: GenerationAsset = {
      id: item.id,
      thumbnailUrl: '',
      type: 'cloud',
      name: item.name,
      sourceUrl: item.preview_url,
      contentType: item.content_type,
      durationSeconds: item.duration_seconds,
      audioText: item.description || item.name,
      defaultAudioRole: item.default_audio_role,
      sourceKind: 'audio_catalog',
    };
    addAsset(asset);
  };

  return createPortal(
    <div className="modal-overlay" onMouseDown={onClose} role="dialog" aria-modal="true" aria-label="SEカタログから選ぶ">
      <div className="modal-card modal-card--md" onMouseDown={(e) => e.stopPropagation()}>
        <header className="modal-header">
          <h2 className="modal-title">SEカタログから選ぶ</h2>
          <button type="button" className="modal-close-button" onClick={onClose} aria-label="閉じる">
            ✕
          </button>
        </header>
        <div className="modal-body">
          {isLoading && <AudioTrackTableMessage>読み込み中…</AudioTrackTableMessage>}
          {isError && (
            <AudioTrackTableMessage>一覧の取得に失敗しました。時間をおいて再度お試しください</AudioTrackTableMessage>
          )}
          {!isLoading && !isError && items.length === 0 && (
            <AudioTrackTableMessage>現在利用できる音声がありません</AudioTrackTableMessage>
          )}
          {!isLoading && !isError && items.length > 0 && (
            <AudioTrackTable
              rows={items.map((item) => {
                const added = selectedCatalogIds.has(item.id);
                const unavailable = !item.preview_url;
                return {
                  id: item.id,
                  name: item.name,
                  subText: item.description,
                  badge: item.default_audio_role ? AUDIO_USAGE_LABELS[item.default_audio_role] : null,
                  durationSeconds: item.duration_seconds,
                  previewUrl: item.preview_url,
                  selected: added,
                  status: added ? '追加済み' : null,
                  disabled: added || unavailable,
                  disabledReason: unavailable ? '現在この音声は利用できません' : undefined,
                  // 複数追加する用途なので、行のクリックで追加してもモーダルは閉じない
                  onSelect: () => handleAdd(item),
                };
              })}
            />
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
